import { useEffect, useState } from 'react';
import { PopupStorageService } from '../services/PopupStorageService';

/**
 * usePopupQueue - Custom hook for managing popup queue and filtering
 * 
 * Implements Single Responsibility Principle by extracting all popup
 * filtering and queue management logic from the component.
 * 
 * @param {Array} notifications - Array of notification objects
 * @param {Object} storageService - Storage service (default: PopupStorageService)
 * @returns {Object} - Current popup and queue management functions
 */
export const usePopupQueue = (
    notifications,
    storageService = PopupStorageService
) => {
    const [currentPopup, setCurrentPopup] = useState(null);
    const [show, setShow] = useState(false);
    const [processedIds, setProcessedIds] = useState(new Set());

    useEffect(() => {
        // Filter notifications to find the next valid popup
        const nextPopup = notifications.find(n => {
            // Skip already processed popups in this session component lifecycle
            if (processedIds.has(n.id)) return false;

            // Filter by Date
            const now = new Date();
            const start = n.datos.fechaInicio ? new Date(n.datos.fechaInicio) : null;
            const end = n.datos.fechaFin ? new Date(n.datos.fechaFin) : null;

            if (start && now < start) return false;
            if (end && now > end) return false;

            // Filter by session storage for session-based popups
            if (n.datos.frecuencia === 1) {
                if (storageService.hasBeenSeen(n.id)) return false;
            }

            return true;
        });

        if (nextPopup) {
            setCurrentPopup(nextPopup);
            setShow(true);
        }
    }, [notifications, processedIds, storageService]);

    const markAsProcessed = (id) => {
        setProcessedIds(prev => {
            const newSet = new Set(prev);
            newSet.add(id);
            return newSet;
        });
    };

    return {
        currentPopup,
        show,
        setShow,
        markAsProcessed,
        hasMore: processedIds.size < notifications.length
    };
};
